import { isValid, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { formatCurrencyAmount } from '@/common/domain/kit/money';
import {
  DEFAULT_VIEWER_LOCALE,
  type ViewerDateFormat,
  type ViewerLocale,
  type ViewerNumberFormat,
  type ViewerTimeFormat
} from '@/viewer-locale/domain/ViewerLocale';
import { getViewerLocale } from '@/viewer-locale/application/viewerLocaleStore';

/**
 * Date-only patterns. The date table still mirrors `customerLocaleFormatters` so both stay in
 * step for date order. Customer-facing output keeps its own date-derived time format because
 * customer locale is resolved from the customer's address country, not from the organization
 * (LLAMS-469 D4) — do not change `customerLocaleFormatters.ts` to match this file's time split.
 */
const DATE_PATTERNS: Record<ViewerDateFormat, string> = {
  UsMonthDayYear: 'MM/dd/yyyy',
  UkDayMonthYear: 'dd/MM/yyyy',
  IsoYearMonthDay: 'yyyy-MM-dd'
};

const TIME_PATTERNS: Record<ViewerTimeFormat, string> = {
  TwelveHour: 'h:mm a',
  TwentyFourHour: 'HH:mm'
};

const DATE_TIME_DATE_PART: Record<ViewerDateFormat, string> = {
  UsMonthDayYear: 'MMM d, yyyy',
  UkDayMonthYear: 'd MMM yyyy',
  IsoYearMonthDay: 'yyyy-MM-dd'
};

/** Culture tags used when `cultureTag` is blank — mirrors customer number-format tables. */
const NUMBER_FORMAT_CULTURE: Record<ViewerNumberFormat, string> = {
  UsCommaDot: 'en-US',
  EuropeanDotComma: 'de-DE',
  FrenchCanadianSpaceComma: 'fr-CA'
};

function dateTimePattern(locale: ViewerLocale): string {
  return `${DATE_TIME_DATE_PART[locale.dateFormat]}, ${TIME_PATTERNS[locale.timeFormat]}`;
}

function toValidDate(value: string | Date): Date | null {
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  const trimmed = value?.trim() ?? '';
  if (!trimmed) return null;

  try {
    const iso = parseISO(trimmed);
    if (isValid(iso)) return iso;
  } catch {
    // fall through to the permissive constructor below
  }

  const fallback = new Date(trimmed);
  return isValid(fallback) ? fallback : null;
}

/**
 * Formats a stored UTC timestamp as a date in the viewer's preferred order and zone.
 * Returns '' for unparseable input — callers render a blank label rather than "Invalid Date".
 */
export function formatViewerDate(value: string | Date, locale: ViewerLocale = getViewerLocale()): string {
  const date = toValidDate(value);
  if (!date) return '';

  try {
    return formatInTimeZone(date, locale.timeZone, DATE_PATTERNS[locale.dateFormat]);
  } catch {
    // Unknown IANA zone (bad host push): fall back to the default zone rather than throwing.
    return formatInTimeZone(date, DEFAULT_VIEWER_LOCALE.timeZone, DATE_PATTERNS[locale.dateFormat]);
  }
}

/** Same contract as `formatViewerDate`, including the wall-clock time in the viewer's zone. */
export function formatViewerDateTime(value: string | Date, locale: ViewerLocale = getViewerLocale()): string {
  const date = toValidDate(value);
  if (!date) return '';

  const pattern = dateTimePattern(locale);
  try {
    return formatInTimeZone(date, locale.timeZone, pattern);
  } catch {
    return formatInTimeZone(date, DEFAULT_VIEWER_LOCALE.timeZone, pattern);
  }
}

/**
 * Formats a stored UTC timestamp as time-of-day only (12h / 24h from `timeFormat`)
 * in the viewer's display zone.
 */
export function formatViewerTime(value: string | Date, locale: ViewerLocale = getViewerLocale()): string {
  const date = toValidDate(value);
  if (!date) return '';

  const pattern = TIME_PATTERNS[locale.timeFormat];
  try {
    return formatInTimeZone(date, locale.timeZone, pattern);
  } catch {
    return formatInTimeZone(date, DEFAULT_VIEWER_LOCALE.timeZone, pattern);
  }
}

/** Plain number formatting using the viewer's culture / number-format preset. */
export function formatViewerNumber(value: number, locale: ViewerLocale = getViewerLocale(), options?: Intl.NumberFormatOptions): string {
  if (!Number.isFinite(value)) return '';
  const culture = locale.cultureTag.trim() || NUMBER_FORMAT_CULTURE[locale.numberFormat];
  return new Intl.NumberFormat(culture, options).format(value);
}

/**
 * Currency amount using the organization currency from the viewer locale
 * (and the viewer's cultureTag for separators / symbol placement).
 */
export function formatViewerCurrency(amount: number, locale: ViewerLocale = getViewerLocale()): string {
  if (!Number.isFinite(amount)) return '';
  const culture = locale.cultureTag.trim() || NUMBER_FORMAT_CULTURE[locale.numberFormat];
  return formatCurrencyAmount(amount, locale.currency, culture);
}
